import { buildSubscriptionPayload } from './subscriptionPayload';
import { buildEventTypePreferences } from './subscriptionTypes';

/**
 * Recursively produces a canonical, key-sorted JSON string. Object key order and array
 * order both carry no meaning in the subscription payload, but `JSON.stringify` is
 * order-sensitive, so both are normalized here.
 */
const canonicalize = (value) => {
    if (Array.isArray(value)) {
        return `[${value.map(canonicalize).sort().join(',')}]`;
    }
    if (value && typeof value === 'object') {
        const entries = Object.keys(value)
            .sort()
            .map((key) => `${JSON.stringify(key)}:${canonicalize(value[key])}`);
        return `{${entries.join(',')}}`;
    }
    return JSON.stringify(value);
};

/**
 * Serializes the modal's editing state into a stable string for pristine/dirty comparison.
 *
 * Deliberately diffs the **wire payload** rather than the raw `checkedProducts` state.
 * `checkedProducts` carries representational redundancy that is not user intent: for
 * single-plane products `control` mirrors `regions` (see `hydrateCheckedProducts`), and
 * `handleChangeRegions` writes `regions` alongside the specific plane key. Comparing raw
 * state would surface those internal mirrorings as spurious "changes". The payload is
 * what actually gets POSTed, so payload equality is exactly the right definition of
 * "nothing to save".
 *
 * Canonicalization matters because neither builder is order-stable:
 *   - `buildSubscriptionPayload` iterates `Object.keys(checkedProducts)` in insertion
 *     order, so adding then removing a product can reorder the result.
 *   - `buildRegions` comma-joins city names in selection order, so picking the same
 *     cities in a different sequence yields `"Ashburn,Chicago"` vs `"Chicago,Ashburn"`.
 *
 * The comma-joined region strings are therefore split, sorted, and rejoined before
 * hashing -- without that, re-selecting an identical region set in a different order
 * would wrongly mark the form dirty.
 */
export const serializeSubscriptionState = (checkedProducts, subscriptionTypes, products) => {
    const subscription = buildSubscriptionPayload(checkedProducts, products);

    const normalizedSubscription = Object.fromEntries(
        Object.entries(subscription).map(([productType, planes]) => [
            productType,
            Object.fromEntries(
                Object.entries(planes).map(([planeType, planeValue]) => [
                    planeType,
                    {
                        regions: (planeValue?.regions || []).map((regionGroup) => Object.fromEntries(
                            Object.entries(regionGroup).map(([key, joined]) => [
                                key,
                                String(joined).split(',').sort().join(','),
                            ]),
                        )),
                    },
                ]),
            ),
        ]),
    );

    return canonicalize({
        subscription: normalizedSubscription,
        ...buildEventTypePreferences(subscriptionTypes),
    });
};

export default serializeSubscriptionState;
